import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "./client";

export type EmployeeStatus = "ACTIVO" | "INACTIVO" | "TERMINADO";
export type ContractType = "TERMINO_FIJO" | "TERMINO_INDEFINIDO" | "OBRA_LABOR" | "PRESTACION_SERVICIOS";

export const EMPLOYEE_STATUS_LABEL: Record<EmployeeStatus, string> = {
  ACTIVO: "Activo",
  INACTIVO: "Inactivo",
  TERMINADO: "Terminado",
};

export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  TERMINO_FIJO: "Término fijo",
  TERMINO_INDEFINIDO: "Término indefinido",
  OBRA_LABOR: "Obra o labor",
  PRESTACION_SERVICIOS: "Prestación de servicios",
};

export interface EmployeeRecord {
  id: string;
  clientId: string | null;
  fullName: string;
  documentType: string;
  documentNumber: string;
  position: string | null;
  contractType: ContractType;
  baseSalary: string;
  status: EmployeeStatus;
  startDate: string;
  endDate: string | null;
  email: string | null;
  phone: string | null;
  eps: string | null;
  pensionFund: string | null;
  arl: string | null;
  compensationFund: string | null;
  bankAccount: string | null;
  notes: string | null;
  createdAt: string;
  client?: { id: string; fullName: string | null; businessName: string | null; tradeName: string | null; clientType: string } | null;
}

export interface ListEmployeesFilters {
  page?: number;
  pageSize?: number;
  clientId?: string;
  status?: EmployeeStatus;
  contractType?: ContractType;
  search?: string;
}

export function useEmployees(filters: ListEmployeesFilters = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["employees", filters],
    enabled: options.enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiSuccessResponse<EmployeeRecord[]>>("/employees", { params: filters });
      return data;
    },
  });
}

export interface CreateEmployeePayload {
  clientId?: string;
  fullName: string;
  documentType: string;
  documentNumber: string;
  position?: string;
  contractType: ContractType;
  baseSalary: number | string;
  status?: EmployeeStatus;
  startDate: string;
  endDate?: string;
  email?: string;
  phone?: string;
  eps?: string;
  pensionFund?: string;
  arl?: string;
  compensationFund?: string;
  bankAccount?: string;
  notes?: string;
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEmployeePayload) => {
      const { data } = await apiClient.post<ApiSuccessResponse<EmployeeRecord>>("/employees", payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["employees"] }),
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<CreateEmployeePayload> & { id: string }) => {
      const { data } = await apiClient.patch<ApiSuccessResponse<EmployeeRecord>>(`/employees/${id}`, payload);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["employees"] }),
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/employees/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["employees"] }),
  });
}

export function employeeClientName(employee: EmployeeRecord): string | null {
  if (!employee.client) return null;
  return employee.client.clientType === "PERSONA_JURIDICA"
    ? employee.client.businessName || employee.client.tradeName || "Cliente"
    : employee.client.fullName || "Cliente";
}
